/**
 * Single source of the pay-range status vocabulary (ER-12848).
 *
 * Range Status:   Draft / In Force / Retired   (+ an "Archived" lifecycle tag, never a Status)
 * Version Status: Draft / In Force / Retired / Voided
 *
 * `tone` is a PayPlanLifecycleBadge status key, so a badge renders as
 * `<PayPlanLifecycleBadge :status="badge.tone" :label="badge.label" />`.
 * State (Lapsed / Current / Future) is a separate concept and is not mapped here.
 */

export const PAY_RANGE_STATUS = Object.freeze({
  DRAFT: "DRAFT",
  IN_FORCE: "IN_FORCE",
  RETIRED: "RETIRED",
  VOIDED: "VOIDED",
});

const BADGES = Object.freeze({
  [PAY_RANGE_STATUS.DRAFT]: Object.freeze({ label: "Draft", tone: "draft" }),
  [PAY_RANGE_STATUS.IN_FORCE]: Object.freeze({ label: "In Force", tone: "in-force" }),
  [PAY_RANGE_STATUS.RETIRED]: Object.freeze({ label: "Retired", tone: "terminated" }),
  [PAY_RANGE_STATUS.VOIDED]: Object.freeze({ label: "Voided", tone: "voided" }),
});

const STATUS_KEYS = Object.freeze({
  DRAFT: PAY_RANGE_STATUS.DRAFT,
  PROPOSED: PAY_RANGE_STATUS.DRAFT,
  NEW_RANGE_PROPOSAL: PAY_RANGE_STATUS.DRAFT,
  PROPOSED_CHANGE: PAY_RANGE_STATUS.DRAFT,
  IN_FORCE: PAY_RANGE_STATUS.IN_FORCE,
  PUBLISHED: PAY_RANGE_STATUS.IN_FORCE,
  ACTIVE: PAY_RANGE_STATUS.IN_FORCE,
  RETIRED: PAY_RANGE_STATUS.RETIRED,
  TERMINATED: PAY_RANGE_STATUS.RETIRED,
  VOIDED: PAY_RANGE_STATUS.VOIDED,
});

const ARCHIVED_TAG = Object.freeze({ label: "Archived" });

function statusToken(input) {
  const raw = typeof input === "string" ? input : input?.status;
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");
}

function isRangeArchived(range) {
  if (!range || typeof range === "string") return statusToken(range) === "ARCHIVED";
  return statusToken(range) === "ARCHIVED" || Boolean(range.archived_at || range.archived_on);
}

/** Underlying Status key of a range, or null when the payload carries none. */
export function normalizeRangeStatus(range) {
  const token = statusToken(range);
  if (token === "ARCHIVED") {
    const retired = typeof range === "object" && (range.retired_at || range.retired_on);
    return retired ? PAY_RANGE_STATUS.RETIRED : PAY_RANGE_STATUS.IN_FORCE;
  }
  const key = STATUS_KEYS[token];
  return key && key !== PAY_RANGE_STATUS.VOIDED ? key : null;
}

/** Underlying Status key of a version, or null when the payload carries none. */
export function normalizeVersionStatus(version) {
  if (version && typeof version === "object" && version.voided_at) return PAY_RANGE_STATUS.VOIDED;
  return STATUS_KEYS[statusToken(version)] || null;
}

/** @returns {{ label: string, tone: string } | null} Draft / In Force / Retired */
export function rangeStatusBadge(range) {
  const key = normalizeRangeStatus(range);
  return key ? { ...BADGES[key] } : null;
}

/** @returns {{ label: string } | null} the neutral "Archived" tag, shown after the status badge */
export function archivedTag(range) {
  return isRangeArchived(range) ? { ...ARCHIVED_TAG } : null;
}

/** @returns {{ label: string, tone: string } | null} Draft / In Force / Retired / Voided */
export function versionStatusBadge(version) {
  const key = normalizeVersionStatus(version);
  return key ? { ...BADGES[key] } : null;
}
